import type { ReactNode } from 'react';
import styled from 'styled-components';

export const PricingWrapper = styled.section`
  display: flex;
  flex-direction: column;
`;

export const PricingDisclaimer = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.gray};
  text-align: center;
  margin-top: 24px;
  margin-bottom: 0;

  a {
    color: ${({ theme }) => theme.palette.gray};
    text-decoration: underline;
  }
`;

export const Title = styled.h2`
  font-size: 32px;
  margin-bottom: 48px;
  text-align: center;
  line-height: 1.2;

  @media ${({ theme }) => theme.media.desktop} {
    font-size: 32px;
  }
`;

export const PricingGrid = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;
  max-width: 400px;
  margin: 0 auto;

  @media ${({ theme }) => theme.media.desktop} {
    grid-template-columns: repeat(3, 1fr);
    max-width: 1200px;
  }
`;

export const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const CartIcon = styled.svg<{
  children?: ReactNode;
  viewBox?: string;
}>`
  width: 18px;
  height: 18px;
  fill: white;
  flex-shrink: 0;
`;

export const Badge = styled.div`
  background: white;
  color: #333;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const PricingCard = styled.div<{
  children: ReactNode;
  highlighted?: boolean;
}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  padding: 36px 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  margin-top: 16px;
  border: ${({ theme, highlighted }) =>
    highlighted
      ? `2px solid ${theme.palette.primary}`
      : '1px solid ${theme.palette.lightGray}'};

  transition: all 0.2s ease;
  ${({ highlighted }) =>
    highlighted &&
    `
    transform: scale(1.02);
  `}
  ${({ highlighted }) =>
    highlighted &&
    `
    strong, h3 {
      font-weight: 700;
    }
  `}

  &::before {
    content: attr(data-badge);
    position: absolute;
    width: fit-content;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background-color: white;
    color: ${({ theme }) => theme.palette.text};
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: ${({ highlighted }) => (highlighted ? '700' : '500')};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
  }
`;

export const PlanName = styled.h3`
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.2;
`;

export const PlanDescription = styled.p`
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.gray};
  margin: 0 0 16px;
  line-height: 1.4;
`;

export const Price = styled.div`
  text-align: center;
  margin-bottom: 24px;
  position: relative;
  width: fit-content;
  margin: 0 auto;
  margin-bottom: 24px;
`;

export const PriceAmount = styled.span`
  font-size: 24px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.gray};
`;

export const DiscountBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
  margin-left: 8px;
  white-space: nowrap;
  display: inline-block;
  background-color: ${({ theme }) => theme.palette.primary};
  color: white;
  font-size: 13px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
`;

export const PricePeriod = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.gray};
`;

export const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: auto;
  padding-top: 24px;

  a {
    display: block;
    width: 100%;
    text-align: center;
  }

  @media ${({ theme }) => theme.media.desktop} {
    align-items: center;
    gap: 6px;

    a {
      display: inline-block;
      width: auto;
    }
  }
`;

export const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  height: 180px;
  margin-bottom: 32px;
`;

export const DetailsButton = styled.button<{
  children: ReactNode;
  onClick: () => void;
}>`
  align-self: center;
  background: none;
  border: none;
  padding: 8px 0;
  margin-top: 16px;
  font-size: 14px;
  color: #444;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.palette.text};
  }
`;

export const SubitemsList = styled.ul`
  list-style: none;
  padding-left: 32px;
  margin: 8px 0;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const SubItem = styled.li<{
  children: ReactNode;
}>`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.gray};
  margin: 4px 0;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: -12px;
  }
`;

export const FeatureItem = styled.li<{
  children?: ReactNode;
}>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text};

  > span:first-child {
    display: inline-flex;
    align-items: flex-start;
    gap: 8px;

    > span[role='img'] {
      margin-top: 2px;
    }

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;

      > strong {
        font-weight: 600;
        font-size: 14px;
      }

      > div {
        font-size: 12px;
        color: ${({ theme }) => theme.palette.gray};
      }
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ variant, theme }) =>
    variant === 'primary'
      ? `
    background-color: ${theme.palette.primary};
    color: white;
    &:hover {
      background-color: ${theme.palette.primaryDark};
    }
  `
      : `
    background-color: ${theme.palette.superLightGray};
    color: ${theme.palette.text};
    &:hover {
      background-color: ${theme.palette.lightGray};
    }
  `}
`;

export const ComparedAtPrice = styled.span`
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  margin-right: 8px;
  white-space: nowrap;
  text-decoration: line-through;
  color: ${({ theme }) => theme.palette.gray};
  font-size: 16px;
`;

export const PriceWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const CountdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
  padding: 24px;
  background-color: ${({ theme }) => theme.palette.superLightGray};
  border-radius: 8px;
  gap: 16px;
`;

export const CountdownTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

export const CountdownTimer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
`;

export const CountdownItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
`;

export const CountdownValue = styled.span`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.primary};
  line-height: 1;
`;

export const CountdownLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.gray};
  margin-top: 4px;
  text-transform: uppercase;
`;

export const DiscountSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

export const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  label {
    font-size: 16px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.dark};
    cursor: pointer;
  }
`;

export const SwitchLabel = styled.label`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.dark};
  cursor: pointer;
`;

export const Switch = styled.label<{
  children?: ReactNode;
  isChecked: boolean;
}>`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ isChecked, theme }) =>
      isChecked ? theme.palette.primary : theme.palette.lightGray};
    transition: 0.3s;
    border-radius: 28px;

    &:before {
      position: absolute;
      content: '';
      height: 20px;
      width: 20px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
      transform: ${({ isChecked }) =>
        isChecked ? 'translateX(22px)' : 'translateX(0)'};
    }
  }
`;

export const DiscountCodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const DiscountCodeBox = styled.div<{
  children?: ReactNode;
  isVisible?: boolean;
  onClick?: () => void;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: white;
  border: 2px solid ${({ theme }) => theme.palette.primary};
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.3s ease, max-height 0.2s ease, padding 0.3s ease,
    margin 0.3s ease;
  overflow: hidden;
  opacity: 1;
  max-height: 200px;
  margin-top: 0;
  pointer-events: auto;

  ${({ isVisible }) => {
    if (!isVisible) {
      return `
        opacity: 0;
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
        margin-top: 0;
        margin-bottom: 0;
        pointer-events: none;
      `;
    }
  }}

  &:hover {
    background-color: ${({ theme }) => theme.palette.superLightGray};
  }
`;

export const DiscountCode = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primary};
  font-family: monospace;
`;

export const CopyIcon = styled.svg<{ children?: ReactNode; viewBox?: string }>`
  width: 18px;
  height: 18px;
  fill: ${({ theme }) => theme.palette.primary};
`;

export const SuccessMessage = styled.div<{
  children?: ReactNode;
  isVisible: boolean;
}>`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.primary};
  text-align: center;
  transition: opacity 0.3s ease, max-height 0.3s ease, margin 0.3s ease;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  max-height: ${({ isVisible }) => (isVisible ? '50px' : '0')};
  overflow: hidden;
  margin: ${({ isVisible }) => (isVisible ? '0' : '-8px 0 0 0')};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
`;

export const OldPrice = styled.span`
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  margin-right: 8px;
  white-space: nowrap;
  text-decoration: line-through;
  color: ${({ theme }) => theme.palette.gray};
  font-size: 24px;
  font-weight: 400;
`;

export const DiscountedPrice = styled.span`
  font-size: 24px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.primary};
`;

export const PriceNote = styled.div`
  font-size: 11px;
  color: #999;
  text-align: center;
  margin-top: 8px;
  font-style: italic;
`;
